import { createDatadogFeatureFlags } from '@ecs-na/access-flag-react-adapter';
import {
  EPPO_API_KEY as PACKAGED_EPPO_API_KEY,
  EppoFeatureFlags,
  type FeatureFlags
} from '@ecs-na/access-flag-service';
import DD_OPTIONS from '../data-dog-options';
import { createAssignmentLogger } from './assignment-logger';
import { DEFAULT_FLAG_PROVIDER, FLAG_ROUTES } from './flag-keys';

// The Eppo browser SDK key is baked into @ecs-na/access-flag-service at the
// library's build time and re-exported as PACKAGED_EPPO_API_KEY, so no key is
// required from this repo. GATSBY_EPPO_API_KEY is an optional build-time
// override for pointing a specific environment at a different Eppo key; when
// unset the packaged key is used. It is a public client-side key either way.
const EPPO_API_KEY = process.env.GATSBY_EPPO_API_KEY || PACKAGED_EPPO_API_KEY;
const isDevelopment = process.env.GATSBY_ENV === 'development';

// TEMP (EXP-101 dev validation): Datadog is normally gated off in `develop`
// (mirroring RUM). Setting GATSBY_FF_DATADOG_DEV=1 lets the Datadog provider
// initialise locally so we can smoke-test the connection. Remove before merge.
const enableDatadogInDev = process.env.GATSBY_FF_DATADOG_DEV === '1';

let eppoProvider: EppoFeatureFlags | null = null;

/** Resolves once Eppo alone has finished init (success or failure), independent of Datadog. */
export const eppoReady = (): Promise<void> => eppoProvider?.ready() ?? Promise.resolve();

/**
 * Datadog config source for the flag provider.
 *
 * Reuses the public RUM credentials already shipped in DD_OPTIONS instead of
 * introducing new ones, so flag exposures report under the same Datadog app as
 * RUM. Returns nothing on the server or in development so the composite falls
 * back to Eppo-only there — mirroring how RUM itself is gated in gatsby-browser.
 * `createDatadogFeatureFlags` validates this shape via `readDatadogRumConfig`.
 */
function readFlagDatadogConfig(): unknown {
  if ((isDevelopment && !enableDatadogInDev) || typeof window === 'undefined') {
    return undefined;
  }

  return {
    clientToken: DD_OPTIONS.clientToken,
    applicationId: DD_OPTIONS.applicationId,
    site: DD_OPTIONS.site,
    env: DD_OPTIONS.env
  };
}

/**
 * Builds the Eppo provider. Invoked once, on mount, by `createDatadogFeatureFlags`'s
 * returned factory — never at import time and never on the server.
 */
function createEppoProvider(): FeatureFlags {
  if (isDevelopment && !EPPO_API_KEY) {
    // eslint-disable-next-line no-console
    console.warn(
      '[feature-flags] No Eppo API key (packaged key empty and GATSBY_EPPO_API_KEY unset) — ' +
        'Eppo flags will return their defaults.'
    );
  }

  eppoProvider = new EppoFeatureFlags({
    apiKey: EPPO_API_KEY,
    assignmentLogger: createAssignmentLogger()
  });

  return eppoProvider;
}

/**
 * App-wide feature-flag factory. Passed to `FlagProvider`'s `flags` prop, which
 * invokes it exactly once on mount. Constructing here (not at import) keeps this
 * module free of `window`/global access, so it is safe to import during SSR.
 *
 * When the Datadog config resolves it composes Eppo + Datadog (routing per
 * FLAG_ROUTES, default FLAG_PROVIDER); otherwise every flag evaluates against
 * Eppo. The single Datadog provider it may build binds the global OpenFeature
 * singleton, which is why exactly one FlagProvider is mounted per site.
 */
export const flagsFactory = createDatadogFeatureFlags({
  eppo: createEppoProvider,
  routes: FLAG_ROUTES,
  defaultProvider: DEFAULT_FLAG_PROVIDER,
  configSource: readFlagDatadogConfig,
  onStatus: (message) => {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.debug('[feature-flags]', message);
    }
  },
  onDatadogInitError: (error) => {
    // eslint-disable-next-line no-console
    console.error('[feature-flags] datadog provider init failed', error);
  }
});
